import { Label, Stack } from '@fluentui/react';
import {
	IDynamicPerson,
	People,
	PeoplePicker,
	Person,
	PersonType,
} from '@microsoft/mgt-react';
import { observer } from 'mobx-react';
import React from 'react';

export interface IPeoplePickerCtrlProps {
	single?: boolean;
	disabled?: boolean;
	label?: string;
	readonly?: boolean;
	selectedIds: string[];
	onSelectionChanged?: (ids: string[]) => void;
	selectionType?: SelectionType;
}

interface IPeoplePickerCtrlState {
	selectedPeople?: IDynamicPerson[];
	isFirstRender: boolean;
}

export enum SelectionType {
	Any,
	User,
	Group,
}

class PeoplePickerCtrl extends React.Component<
	IPeoplePickerCtrlProps,
	IPeoplePickerCtrlState
> {
	constructor(props: IPeoplePickerCtrlProps) {
		super(props);
		this.state = {
			isFirstRender: true,
		};
	}

	render() {
		let selectionType = PersonType.any;
		if (this.props.selectionType === SelectionType.User) {
			selectionType = PersonType.person;
		} else if (this.props.selectionType === SelectionType.Group) {
			selectionType = PersonType.group;
		}

		let selectedIds = (this.props.selectedIds || []).filter((s) => !!s);

		let groupIds: string[] = [];
		let userIds: string[] = [];
		let emailAddresses: string[] = [];
		let displayValues: string[] = [];

		selectedIds.forEach((selected) => {
			if (selected) {
				if (selected.startsWith('g|')) {
					groupIds.push(selected);
				} else if (selected.length === 36) {
					userIds.push(selected);
				} else if (selected.indexOf('@') > 0) {
					emailAddresses.push(selected);
				} else {
					displayValues.push(selected);
				}
			}
		});

		if (displayValues.length) {
			return displayValues.map((d) => <span>{d}</span>);
		}

		return (
			<>
				{this._getReadonlyPeople()}

				<div style={this.props.readonly ? { display: 'none' } : {}}>
					{this.getLabel()}
					<PeoplePicker
						className="peoplePickerDisplay"
						style={{ borderWidth: 1 }}
						selectionMode={this.props.single ? 'single' : 'multiple'}
						disabled={this.props.disabled}
						allowAnyEmail={true}
						selectedPeople={this.state.selectedPeople}
						defaultSelectedUserIds={this.state.selectedPeople ? undefined : userIds}
						defaultSelectedGroupIds={this.state.selectedPeople ? undefined : groupIds}
						type={selectionType}
						selectionChanged={(e: any) => {
							let selection = (e.detail as IDynamicPerson[]) || [];

							//If this is the very first render, then email addresses may be missing, so we have to set them programmatically.
							if (this.state.isFirstRender && emailAddresses.length) {
								emailAddresses.forEach((email) => {
									let isAlreadySelected = selection.find(
										(s) => (s as any).mail === email,
									);
									if (!isAlreadySelected) {
										selection = selection.concat([{ mail: email, displayName: email }]);
									}
								});
							}

							this.setState({
								selectedPeople: selection,
								isFirstRender: false,
							});

							//Prepend a 'g|' to the groups. groups are either security groups (securityEnabled) or M365 Groups (groupType is set to 'unified')
							let groupIds = selection
								.filter(
									(s: any) =>
										(s.groupTypes && s.groupTypes.length > 0) || s.securityEnabled,
								)
								.map((s) => s.id)
								.filter((id) => !!id);
							let userIds = selection
								.map((s) => s.id)
								.filter((id) => !!id && groupIds.indexOf(id) < 0)
								.map((id) => id || '');
							let emails = selection
								.filter((s) => !s.id)
								.map((s) => (s as any).mail)
								.filter((mail) => !!mail);

							let processedIds = groupIds
								.map((gId) => 'g|' + gId)
								.concat(userIds)
								.concat(emails);

							if (
								this.props.onSelectionChanged &&
								this.areDifferent(processedIds, this.props.selectedIds)
							) {
								this.props.onSelectionChanged(processedIds);
							}
						}}
					/>
				</div>
			</>
		);
	}

	getLabel(): React.ReactNode {
		if (this.props.label) {
			return <Label>{this.props.label}</Label>;
		}
	}

	private areDifferent(processedIds: string[], selectedIds: string[]): boolean {
		if (processedIds.length !== selectedIds.length) {
			return true;
		}

		for (let i = 0; i < processedIds.length; i++) {
			const id = processedIds[i];
			if (!selectedIds.find((selectedId) => selectedId === id)) return true;
		}

		return false;
	}

	private _getReadonlyPeople() {
		if (this.props.readonly) {
			if (this.props.single) {
				if ((this.state.selectedPeople?.length || 0) > 0) {
					let person: IDynamicPerson = (this.state.selectedPeople || [])[0];

					return (
						<Stack horizontal>
							<Person
								fetchImage={true}
								personImage={person.personImage}
								personDetails={person}
							></Person>
							<Label style={{ marginLeft: 5 }}>{person?.displayName}</Label>
						</Stack>
					);
				}
			} else {
				if (this.state.selectedPeople) {
					return (
						<People
							key={this.getPeopleKey(this.state.selectedPeople)}
							people={this.state.selectedPeople}
						></People>
					);
				}
			}
		}

		return <></>;
	}

	private getPeopleKey(selectedPeople: IDynamicPerson[]): string {
		if (!selectedPeople || selectedPeople.length === 0) {
			return 'ppl-empty';
		}

		return selectedPeople.map((p) => p.id).join(',');
	}
}

export default observer(PeoplePickerCtrl);
